import type { Edge, Node } from '@xyflow/react';
import { MarkerType } from '@xyflow/react';
import type { CustomerJourney } from '../../types/technicianJourney';
import type { JourneyHubNodeData, JourneyMetricNodeData, JourneyPhaseNodeData } from './buildJourneyFlow';

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return s;
  }
}

const Y = 80;
const GAP = 200;

export function buildCustomerJourneyFlow(c: CustomerJourney): { nodes: Node[]; edges: Edge[] } {
  const qi = c.quoteInteractions;

  const hub: Node<JourneyHubNodeData> = {
    id: 'cust-hub',
    type: 'journeyHub',
    position: { x: 20, y: Y },
    data: {
      title: c.name,
      titleAr: 'عميل',
      lines: [
        `#${c.customerId}`,
        c.phoneNumber,
        c.email || '—',
        `Status: ${c.status}`,
      ],
    },
  };

  const reg: Node<JourneyPhaseNodeData> = {
    id: 'cust-reg',
    type: 'journeyPhase',
    position: { x: 20 + GAP, y: Y },
    data: {
      label: 'Registration',
      labelAr: 'التسجيل',
      completed: Boolean(c.registrationDate),
      active: false,
      subtitle: `Reg: ${fmtDate(c.registrationDate)}\nTerms: ${fmtDate(c.acceptedTermsAt)}`,
      variant: c.registrationDate ? 'success' : 'muted',
    },
  };

  const activity: Node<JourneyMetricNodeData> = {
    id: 'cust-activity',
    type: 'journeyMetric',
    position: { x: 20 + GAP * 2, y: Y },
    data: {
      label: 'Login & posts',
      labelAr: 'الدخول والمشاريع',
      accent: 'sky',
      lines: [
        `Last login: ${fmtDate(c.lastLoginDate)}`,
        `Last project: ${fmtDate(c.lastProjectPostedDate)}`,
      ],
    },
  };

  const projects: Node<JourneyMetricNodeData> = {
    id: 'cust-projects',
    type: 'journeyMetric',
    position: { x: 20 + GAP * 3, y: Y },
    data: {
      label: 'Projects',
      labelAr: 'المشاريع',
      accent: 'amber',
      lines: [
        `Total posted: ${c.totalProjectsPosted}`,
        `Last 30 / 60 days: ${c.projectsLast30Days} / ${c.projectsLast60Days}`,
      ],
    },
  };

  const quotes: Node<JourneyMetricNodeData> = {
    id: 'cust-quotes',
    type: 'journeyMetric',
    position: { x: 20 + GAP * 4, y: Y },
    data: {
      label: 'Quote interactions',
      labelAr: 'تفاعل العروض',
      accent: 'violet',
      chainEnd: true,
      lines: [
        `Bids received: ${qi.totalBidsReceived}`,
        `Accepted / rejected / pending: ${qi.acceptedBids} / ${qi.rejectedBids} / ${qi.pendingBids}`,
      ],
    },
  };

  const nodes: Node[] = [hub, reg, activity, projects, quotes];

  const stroke = '#6366f1';
  const edge = (id: string, source: string, target: string): Edge => ({
    id,
    source,
    target,
    animated: true,
    style: { stroke, strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: stroke },
  });

  const edges: Edge[] = [
    { ...edge('c1', 'cust-hub', 'cust-reg'), sourceHandle: 'out', targetHandle: 'in' },
    { ...edge('c2', 'cust-reg', 'cust-activity'), sourceHandle: 'out', targetHandle: 'in' },
    { ...edge('c3', 'cust-activity', 'cust-projects'), sourceHandle: 'out', targetHandle: 'in' },
    { ...edge('c4', 'cust-projects', 'cust-quotes'), sourceHandle: 'out', targetHandle: 'in' },
  ];

  return { nodes, edges };
}
